import { getPhoneDigitsSize } from '@/config/countryCodes';

/**
 * Form validators — mirrors Flutter FormValidators (domain/core/validators/form_validators.dart).
 *
 * Each returns the error message, or '' when the value is valid. Messages are built the way
 * Flutter builds them: every fixed part goes through `t()` with Flutter's exact text, and the
 * parts are joined with the same spaces and punctuation (e.g. `${t('First Name')} ${t('is required')}`).
 */

type Translate = (key: string) => string;

/** Characters Flutter accepts as a password special character (form_validators.dart). */
const PASSWORD_SPECIAL_CHARACTER = /[\^$*.[\]{}()?"!@#%&\/\\,><:;|_~]/;

/** The list Flutter prints after the password messages (it also names `, which it doesn't accept). */
export const PASSWORD_SPECIAL_CHARACTERS_LIST = '^ $ * . [ ] { } ( ) ? " ! @ # % & / \\ , > < : ; | _ ~ `';

/** Flutter WebUploadResume.createPasswordMessage — the password tooltip, shown with specialCharacters after it. */
export const CREATE_PASSWORD_MESSAGE =
  '•Password must contain a lower case letter\n•Password must contain an upper case\n•Password must contain a number\n•Password must contain at least 8 characters\n•Password must contain a special character among the provided below:';

/** Flutter WebUploadResume.specialCharacters */
export const PASSWORD_TOOLTIP_SPECIAL_CHARACTERS = `\n ${PASSWORD_SPECIAL_CHARACTERS_LIST}`;

/** FormValidators.validateText (no maxLen / minLen 1, as the sign-up form uses it). */
export function validateText(value: string, text: string, t: Translate): string {
  if (!value) return `${text} ${t('is required')}`;
  return '';
}

/**
 * Characters a person's name may contain: letters of any script with their combining marks,
 * spaces, apostrophes (' ’), hyphens and periods — "José", "Zoë", "O'Brien", "Jean-Luc",
 * "J. R.", "Nguyễn", "李". Flutter's sign-up form only lets [a-zA-Z\s] through and silently drops
 * anything else; the name is a free-text field on the backend, so valid names are kept here.
 */
const NAME_PATTERN = /^\p{L}[\p{L}\p{M} '’.\-]*$/u;

/** Characters outside NAME_PATTERN — for fields that filter input instead of showing an error. */
export const NAME_DISALLOWED_CHARACTERS = /[^\p{L}\p{M} '’.\-]/gu;

/**
 * Name validation shared by the sign-up first / last name: required (after trimming), then
 * NAME_PATTERN. `label` is the translated field label, as validateText composes it.
 */
export function validateName(value: string, label: string, t: Translate): string {
  const trimmed = value.trim();
  if (!trimmed) return `${label} ${t('is required')}`;
  if (!NAME_PATTERN.test(trimmed)) {
    return `${label} ${t('can only contain letters, spaces, apostrophes, hyphens and periods')}`;
  }
  return '';
}

/**
 * FormValidators.validateMobile — `cc` is the phone code without '+'. An empty value is always
 * "Phone number is required"; beyond that, a code missing from Flutter's digit tables is not
 * validated (Flutter skips even the required check for those codes).
 */
export function validateMobile(value: string, cc: string, t: Translate): string {
  // The mobile number is a required field on every form that uses this, whatever the country
  if (!value) return t('Phone number is required');

  const size = getPhoneDigitsSize(cc);
  if (!size) return '';

  if (!/^(?!.*[\s-]$)(?!.*^[\s-])(?=.*[0-9 -])[0-9 -]+$/.test(value)) {
    if (/^[^\d\s-]/.test(value)) {
      return t('Invalid characters. Only digits, hyphens, and space are allowed.');
    }
    if (/^[\s-]/.test(value) || /[\s-]$/.test(value)) {
      return t('Leading or trailing space or hyphen is not allowed.');
    }
    return t('Invalid phone number format');
  }

  const digits = value.replace(/[ -]/g, '').length;
  if (size.min == null) {
    if (digits !== size.max) return `${t('Phone Number length must be')} ${size.max}`;
  } else if (digits > size.max || digits < size.min) {
    return `${t('Phone Number length must be in between')} ${size.min}-${size.max}`;
  }
  return '';
}

/**
 * FormValidators.isValidEmail — the sign-up email check. An empty value is "Email is required"
 * (Flutter's message for a missing email in validateEmail / isValidConfirmEmail), not
 * "Not a valid email", so every auth form says the same for an empty field.
 */
export function isValidEmail(value: string, t: Translate): string {
  if (!value) return t('Email is required');
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return pattern.test(value) ? '' : t('Not a valid email');
}

// Approximates the email_validator package's EmailValidator.validate() (dot-atom or quoted local
// part; a host name with at least one dot, or an IP literal), which Flutter's validateEmail uses.
const ATEXT = "[A-Za-z0-9!#$%&'*+\\-/=?^_`{|}~\\u0080-\\uFFFF]";
const LABEL = '[A-Za-z0-9\\u0080-\\uFFFF](?:[A-Za-z0-9\\-\\u0080-\\uFFFF]*[A-Za-z0-9\\u0080-\\uFFFF])?';
const EMAIL_VALIDATOR_PATTERN = new RegExp(
  `^(?:${ATEXT}+(?:\\.${ATEXT}+)*|"(?:[^"\\\\]|\\\\.)*")@(?:${LABEL}(?:\\.${LABEL})+|\\[(?:\\d{1,3}\\.){3}\\d{1,3}\\])$`,
);

/**
 * FormValidators.validateEmail — the sign-in and forgot-password email check: "Email is required"
 * when empty (Flutter returns it for a null value; the field always gives '' instead, which used to
 * read "Please enter valid email"), otherwise "Please enter valid email" for a malformed address.
 */
export function validateEmail(value: string, t: Translate): string {
  if (!value) return t('Email is required');
  return EMAIL_VALIDATOR_PATTERN.test(value) ? '' : t('Please enter valid email');
}

/** FormValidators.validatePassword — the sign-in password check. */
export function validatePassword(pass: string, t: Translate): string {
  return pass ? '' : t('Password is required');
}

/** FormValidators.validateAllowedCharactersInPassword */
export function validateAllowedCharactersInPassword(value: string, t: Translate): string {
  for (const c of value.split('')) {
    const allowed = c !== ' ' && (/[a-zA-Z0-9]/.test(c) || PASSWORD_SPECIAL_CHARACTER.test(c));
    if (!allowed) {
      return `${t("Password doesn't match the criteria! Only following special characters are allowed.")}\n${PASSWORD_SPECIAL_CHARACTERS_LIST}`;
    }
  }
  return '';
}

/**
 * FormValidators.validatePasswords — the sign-up and reset-password rules. With `isConfirm`
 * it checks the re-typed password against `pass` instead.
 */
export function validatePasswords(
  { pass, confirmPass = '', isConfirm = false }: { pass: string; confirmPass?: string; isConfirm?: boolean },
  t: Translate,
): string {
  if (isConfirm) {
    if (!confirmPass) return t('Password is required');
    if (confirmPass !== pass) return t("Password doesn't match");
    return '';
  }

  const charactersError = validateAllowedCharactersInPassword(pass, t);
  if (charactersError) return charactersError;

  let errors: string[] = [];
  if (pass.trim() !== pass) errors.push(t('no leading or trailing spaces'));
  if (!/[a-z]/.test(pass)) errors.push(t('lowercase letter'));
  if (!/[A-Z]/.test(pass)) errors.push(t('uppercase letter'));
  if (!/[0-9]/.test(pass)) errors.push(t('number'));
  if (pass.length < 8) errors.push(t('at least 8 characters'));
  if (!PASSWORD_SPECIAL_CHARACTER.test(pass)) errors.push(t('special character'));
  if (pass.trim() !== pass) errors.push(t('leading or trailing spaces'));

  if (errors.length === 0) return '';
  if (errors.join('') === t('special character')) {
    errors = [`${t('at least one special character from the following')}: ${PASSWORD_SPECIAL_CHARACTERS_LIST}`];
  }
  return `${t('Password must contain')} ${errors.join(', ')}`;
}
